import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link2, Loader2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// "2 sheet names don't match a user" → link each sheet spelling to the right person (saved as a known-as alias) and re-read the sheet.
export function UnmatchedNamesFixer({ names, canEdit, onFixed, onClose }) {
  const [people, setPeople] = useState([]);
  const [pick, setPick] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/dailies/picklists`, auth()).then(r => setPeople(r.data?.crew || [])).catch(() => {}); }, []);

  const guess = (n) => {
    const parts = n.toLowerCase().split(/[\s-]+/).filter(Boolean);
    const hits = people.filter(p => {
      const pp = (p.name || '').toLowerCase().split(/[\s-]+/).filter(Boolean);
      return pp[0] && parts[0] && (pp[0] === parts[0] || pp[0].startsWith(parts[0]) || parts[0].startsWith(pp[0])) && pp.slice(1).some(t => parts.slice(1).includes(t));
    });
    return hits.length === 1 ? hits[0].id : '';
  };
  const chosen = (n) => pick[n] ?? guess(n);

  const save = async () => {
    const todo = names.filter(n => chosen(n));
    if (!todo.length) return;
    setBusy(true);
    try {
      for (const n of todo) {
        const p = people.find(x => x.id === chosen(n));
        const current = (await axios.get(`${API}/shop/employee-aliases`, auth())).data.find(r => r.id === p.id)?.aliases || [];
        if (!current.includes(n)) await axios.put(`${API}/shop/employee-aliases/${p.id}`, { aliases: [...current, n] }, auth());
      }
      toast.success(`${todo.length} name${todo.length === 1 ? '' : 's'} linked — re-reading the sheet…`);
      await axios.post(`${API}/manpower/sync`, {}, { ...auth(), timeout: 180000 });
      onFixed?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not link'); }
    setBusy(false);
  };

  return (
    <div className="mt-2 rounded-xl border border-red-200 bg-red-50/60 p-3" data-testid="unmatched-fixer">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs text-red-900"><b>These names on Blair's sheet don't match anyone in Ops</b>, so they can't be shown as working or counted. Pick who each one is — it's remembered as a "known as" name, and the sheet is read again.</p>
        <button onClick={onClose} className="p-0.5 rounded text-red-700 hover:bg-red-100" data-testid="unmatched-fixer-close"><X className="w-4 h-4" /></button>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {names.map(n => (
          <label key={n} className="inline-flex items-center gap-1.5 text-xs bg-white border border-red-200 rounded-lg px-2 py-1" data-testid={`unmatched-row-${n}`}>
            <span className="font-semibold text-slate-900">{n}</span>
            <Link2 className="w-3 h-3 text-slate-400" />
            <select value={chosen(n)} onChange={e => setPick(s => ({ ...s, [n]: e.target.value }))} disabled={!canEdit} className="h-6 text-xs border border-slate-300 rounded px-1 bg-white" data-testid={`unmatched-pick-${n}`}>
              <option value="">is… (nobody / leave)</option>
              {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
        ))}
      </div>
      {canEdit && (
        <button onClick={save} disabled={busy || !names.some(n => chosen(n))} className="mt-2 inline-flex items-center gap-1 h-7 px-3 rounded-lg bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white text-xs font-bold" data-testid="unmatched-fixer-save">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />} Link {names.filter(n => chosen(n)).length || ''} and re-read the sheet
        </button>
      )}
    </div>
  );
}
